import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type AccessPolicy, type PortalUser } from '../lib/api';
import { PolicyEditor } from './PolicyEditor';

interface UserEditModalProps {
  user: PortalUser;
  onClose: () => void;
  onMutate: () => void;
}

export function UserEditModal({ user, onClose, onMutate }: UserEditModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(user.name ?? '');
  const [organization, setOrganization] = useState(user.organization ?? '');
  const [profileSaving, setProfileSaving] = useState(false);

  const [requestStatus, setRequestStatus] = useState(user.requestStatus);
  const [policy, setPolicy] = useState<AccessPolicy | null>(user.accessPolicy ?? null);

  const [pendingPolicy, setPendingPolicy] = useState<AccessPolicy | null>(null);

  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  useEffect(() => {
    setName(user.name ?? '');
    setOrganization(user.organization ?? '');
    setRequestStatus(user.requestStatus);
    setPolicy(user.accessPolicy ?? null);
    setPendingPolicy(null);
    setError('');
    setInfo('');
  }, [user.email]); // eslint-disable-line react-hooks/exhaustive-deps

  function clearMessages() {
    setError('');
    setInfo('');
  }

  async function withBusy<T>(label: string, fn: () => Promise<T>): Promise<T | undefined> {
    setBusy(label);
    clearMessages();
    try {
      const r = await fn();
      onMutate();
      return r;
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed: ${label}`);
      return undefined;
    } finally {
      setBusy(null);
    }
  }

  async function saveProfile() {
    if (!name.trim() && !organization.trim()) return;
    await withBusy('profile', async () => {
      await api.setUserProfile(user.email, { name: name.trim(), organization: organization.trim() });
      setInfo(t('userEditModal.profileSaved'));
    });
    setProfileSaving(false);
  }

  async function approve() {
    if (!user.requestedAt) return;
    const useEntered: AccessPolicy = pendingPolicy ?? policy ?? {
      approvedTypes: ['PCI'],
      rocReportIds: [],
      approvedReportIds: [],
    };
    await withBusy('approve', async () => {
      await api.handleRequest({
        email: user.email,
        requestedAt: user.requestedAt!,
        action: 'APPROVED',
        accessPolicy: useEntered,
      });
      setRequestStatus('APPROVED');
      setPolicy(useEntered);
      setInfo(t('userEditModal.approved'));
    });
  }

  async function deny() {
    if (!user.requestedAt) return;
    if (!confirm(`Deny access for ${user.email}?`)) return;
    await withBusy('deny', async () => {
      await api.handleRequest({
        email: user.email,
        requestedAt: user.requestedAt!,
        action: 'DENIED',
      });
      setRequestStatus('DENIED');
      setInfo(t('userEditModal.denied'));
    });
  }

  async function savePolicy() {
    if (!pendingPolicy) return;
    await withBusy('savePolicy', async () => {
      await api.setAccessPolicy(user.email, pendingPolicy);
      setPolicy(pendingPolicy);
      setInfo(t('userEditModal.policyUpdated'));
    });
  }

  async function disable() {
    if (!confirm(`Disable user ${user.email}?`)) return;
    await withBusy('disable', () => api.disableUser(user.email));
    setInfo(t('userEditModal.userDisabled'));
  }

  async function enable() {
    await withBusy('enable', () => api.enableUser(user.email));
    setInfo(t('userEditModal.userEnabled'));
  }

  async function del() {
    if (!confirm(`Delete user ${user.email}? Identity is removed and pending requests are cleared. This cannot be undone.`)) return;
    const ok = await withBusy('delete', () => api.deleteUser(user.email));
    if (ok) onClose();
  }

  const [isAdminLocal, setIsAdminLocal] = useState(user.isAdmin ?? false);

  useEffect(() => {
    setIsAdminLocal(user.isAdmin ?? false);
  }, [user.isAdmin]);

  const isMidway = user.authMethod === 'midway';

  async function toggleAdmin() {
    const alias = user.adminAlias || user.email.split('@')[0];
    if (isAdminLocal) {
      if (!confirm(`Remove admin access for ${user.email}?`)) return;
      await withBusy('toggleAdmin', async () => {
        await api.demoteAdmin(alias);
        setIsAdminLocal(false);
        setInfo(t('userEditModal.adminRemoved', { defaultValue: 'Admin access removed.' }));
      });
    } else {
      await withBusy('toggleAdmin', async () => {
        await api.promoteAdmin(alias);
        setIsAdminLocal(true);
        setInfo(t('userEditModal.adminGranted', { defaultValue: 'Admin access granted.' }));
      });
    }
  }

  const hasRequest = !!user.requestedAt;

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="user-edit-title">
      <div className="modal-card modal-card-wide">
        <h2 id="user-edit-title">{t('userEditModal.title', { email: user.email })}</h2>

        {error && <div className="alert alert-error" role="alert">{error}</div>}
        {info && <div className="alert alert-info" role="status">{info}</div>}

        {/* Profile */}
        <fieldset className="policy-section">
          <legend>{t('userEditModal.profile')}</legend>
          {hasRequest ? (
            <>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="user-edit-name">{t('common.name')}</label>
                  <input
                    id="user-edit-name"
                    type="text"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      setProfileSaving(true);
                    }}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="user-edit-org">{t('common.organization')}</label>
                  <input
                    id="user-edit-org"
                    type="text"
                    value={organization}
                    onChange={(e) => {
                      setOrganization(e.target.value);
                      setProfileSaving(true);
                    }}
                  />
                </div>
              </div>
              <div className="modal-section-actions">
                <button
                  type="button"
                  className="btn-primary"
                  onClick={saveProfile}
                  disabled={busy !== null || !profileSaving}
                >
                  {busy === 'profile' ? t('userEditModal.savingProfile') : t('userEditModal.saveProfile')}
                </button>
              </div>
            </>
          ) : (
            <p className="form-hint">{t('userEditModal.profileHint')}</p>
          )}
        </fieldset>

        {/* Request lifecycle */}
        {hasRequest && requestStatus !== 'APPROVED' && (
          <fieldset className="policy-section">
            <legend>{t('userEditModal.accessRequest')}</legend>
            <p className="form-hint">
              {t('userEditModal.currentStatus', { status: requestStatus })}
            </p>
            <PolicyEditor
              onChange={setPendingPolicy}
              showRoc
            />
            <div className="modal-section-actions">
              <button
                type="button"
                className="btn-approve"
                onClick={approve}
                disabled={busy !== null}
              >
                {busy === 'approve' ? t('userEditModal.approving') : requestStatus === 'DENIED' ? t('userEditModal.reApprove') : t('userEditModal.approve')}
              </button>
              {requestStatus === 'PENDING' && (
                <button
                  type="button"
                  className="btn-deny"
                  onClick={deny}
                  disabled={busy !== null}
                >
                  {busy === 'deny' ? t('userEditModal.denying') : t('userEditModal.denyBtn')}
                </button>
              )}
            </div>
          </fieldset>
        )}

        {/* Approved policy editor */}
        {requestStatus === 'APPROVED' && (
          <fieldset className="policy-section">
            <legend>{t('userEditModal.documentAccess')}</legend>
            <PolicyEditor
              initial={policy ?? undefined}
              onChange={setPendingPolicy}
              showRoc
            />
            <div className="modal-section-actions">
              <button
                type="button"
                className="btn-primary"
                onClick={savePolicy}
                disabled={busy !== null || !pendingPolicy}
              >
                {busy === 'savePolicy' ? t('userEditModal.savingPolicy') : t('userEditModal.savePolicyBtn')}
              </button>
            </div>
          </fieldset>
        )}

        {/* Account actions */}
        <fieldset className="policy-section">
          <legend>{t('userEditModal.accountSection', { defaultValue: 'Account' })}</legend>
          <div className="form-group">
            {isMidway ? (
              <label className="aoc-check-row">
                <input
                  type="checkbox"
                  checked={isAdminLocal}
                  onChange={toggleAdmin}
                  disabled={busy !== null}
                />
                <span className="aoc-check-label">
                  {t('userEditModal.adminToggleLabel', { defaultValue: 'Portal administrator' })}
                </span>
              </label>
            ) : (
              <p className="form-hint">
                {t('userEditModal.adminMidwayOnly', { defaultValue: 'Only Midway-authenticated users can be administrators.' })}
              </p>
            )}
          </div>
          <div className="modal-section-actions">
            <button type="button" className="btn-deny" onClick={disable} disabled={busy !== null}>
              {busy === 'disable' ? t('userEditModal.disabling') : t('userEditModal.disableAccount')}
            </button>
            <button type="button" className="btn-approve" onClick={enable} disabled={busy !== null}>
              {busy === 'enable' ? t('userEditModal.enabling') : t('userEditModal.enableAccount')}
            </button>
          </div>
        </fieldset>

        {/* Delete */}
        <fieldset className="policy-section">
          <legend>{t('userEditModal.deleteSection')}</legend>
          <p className="form-hint">{t('userEditModal.deleteHint')}</p>
          <div className="modal-section-actions">
            <button type="button" className="btn-deny" onClick={del} disabled={busy !== null}>
              {busy === 'delete' ? t('userEditModal.deleting') : t('userEditModal.deleteUser')}
            </button>
          </div>
        </fieldset>

        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy !== null}>
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
